import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button } from "@vimos/ui";
import { canEdit, useAuth } from "../../lib/auth";
import { fetchDirectoryStore360, publicSiteUrl, updateDirectoryStoreContext } from "./directoryApi";
import { saasManagementUrl } from "./directoryLib";

const saasAdminBaseUrl = import.meta.env.VITE_SAAS_ADMIN_BASE_URL as string | undefined;
const money = new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" });
const dateTime = new Intl.DateTimeFormat("es-PE", { dateStyle: "medium", timeStyle: "short" });
const dateOnly = new Intl.DateTimeFormat("es-PE", { dateStyle: "medium", timeZone: "UTC" });

function formatDate(value: string | null | undefined, withTime = false): string {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : (withTime ? dateTime : dateOnly).format(parsed);
}

function originLabel(origin: string | null | undefined): string {
  return origin === "manual" ? "Manual" : "SaaS";
}

function chargeStatusLabel(status: string): string {
  if (status === "paid") return "Pagado";
  if (status === "void") return "Anulado";
  return "Pendiente";
}

function invitationStatusLabel(status: string): string {
  if (status === "submitted") return "Usada / enviada";
  if (status === "revoked") return "Revocada";
  if (status === "expired") return "Vencida";
  return "Disponible";
}

function submissionStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: "Pendiente",
    received: "Recibida",
    in_review: "En revisión",
    changes_requested: "Cambios solicitados",
    approved: "Aprobada",
    rejected: "Rechazada",
  };
  return labels[status] ?? status;
}

function activityDate(value: string | null | undefined): number {
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function Store360Page() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userCanEdit = canEdit(profile);
  const [notes, setNotes] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [saved, setSaved] = useState(false);

  const detailQuery = useQuery({
    queryKey: ["stores_directory", "360", id],
    queryFn: () => fetchDirectoryStore360(id!),
    enabled: Boolean(id),
  });
  const store = detailQuery.data?.store;

  useEffect(() => {
    if (!store) return;
    setNotes(store.admin_notes ?? "");
    setNextAction(store.admin_next_action ?? "");
    setDueDate(store.admin_next_action_due_date ?? "");
  }, [store?.id, store?.admin_context_updated_at]);

  const saveContext = useMutation({
    mutationFn: () => {
      if (!id) throw new Error("No se encontró el identificador del negocio.");
      return updateDirectoryStoreContext({
        storeId: id,
        notes,
        nextAction,
        dueDate: nextAction.trim() ? dueDate || null : null,
      });
    },
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ["stores_directory", "360", id] });
      await queryClient.invalidateQueries({ queryKey: ["stores_directory"] });
    },
  });

  const activity = useMemo(() => {
    const detail = detailQuery.data;
    if (!detail) return [];
    const items: Array<{ id: string; at: string; title: string; detail: string; kind: "neutral" | "success" | "warning" }> = [];
    const store = detail.store;
    items.push({
      id: `created-${store.id}`,
      at: store.created_at,
      title: "Negocio incorporado al directorio",
      detail: `Origen ${originLabel(store.origin)} · ${store.slug}`,
      kind: "success",
    });
    if (store.admin_context_updated_at) {
      items.push({
        id: `context-${store.id}`,
        at: store.admin_context_updated_at,
        title: "Seguimiento interno actualizado",
        detail: store.admin_next_action ? `Próxima acción: ${store.admin_next_action}` : "Se actualizaron las notas internas.",
        kind: "neutral",
      });
    }
    for (const invitation of detail.invitations) {
      items.push({
        id: `invitation-${invitation.id}`,
        at: invitation.created_at,
        title: invitation.mode === "edit" ? "Enlace de edición generado" : "Invitación de alta generada",
        detail: `${invitation.label || "Sin referencia"} · ${invitationStatusLabel(invitation.status)}${invitation.used_count ? ` · ${invitation.used_count} uso(s)` : ""}`,
        kind: invitation.status === "revoked" || invitation.status === "expired" ? "warning" : "neutral",
      });
      if (invitation.last_used_at) {
        items.push({
          id: `invitation-used-${invitation.id}`,
          at: invitation.last_used_at,
          title: "Enlace utilizado",
          detail: invitation.label || (invitation.mode === "edit" ? "Edición de ficha" : "Alta de ficha"),
          kind: "success",
        });
      }
    }
    for (const submission of detail.submissions) {
      items.push({
        id: `submission-${submission.id}`,
        at: submission.updated_at,
        title: submission.edit_features ? "Cambios enviados a revisión" : "Ficha enviada para revisión",
        detail: `${submissionStatusLabel(submission.status)}${submission.review_notes ? ` · ${submission.review_notes}` : ""}`,
        kind: submission.status === "rejected" ? "warning" : submission.status === "approved" ? "success" : "neutral",
      });
      for (const event of submission.review_events ?? []) {
        items.push({
          id: `review-${event.id}`,
          at: event.created_at,
          title: event.action === "rejected" ? "Solicitud rechazada" : "Solicitud reabierta",
          detail: event.notes || "Sin motivo registrado",
          kind: event.action === "rejected" ? "warning" : "success",
        });
      }
    }
    for (const charge of detail.charges) {
      const chargeLabel = charge.charge_type === "setup" ? "Alta" : charge.charge_type === "edit" ? "Edición" : charge.charge_type === "yearly" ? "Anualidad" : "Mensualidad";
      items.push({
        id: `charge-${charge.id}`,
        at: charge.created_at,
        title: `${chargeLabel} · ${chargeStatusLabel(charge.status)}`,
        detail: `${money.format(Number(charge.amount))} · vence ${formatDate(charge.due_date)}`,
        kind: charge.status === "paid" ? "success" : charge.status === "pending" ? "warning" : "neutral",
      });
      if (charge.last_reminder_at) {
        items.push({
          id: `reminder-${charge.id}`,
          at: charge.last_reminder_at,
          title: "Recordatorio registrado",
          detail: `${charge.reminder_count} aviso(s) en este cobro · ${charge.description}`,
          kind: "neutral",
        });
      }
      if (charge.paid_at) {
        items.push({
          id: `paid-${charge.id}`,
          at: charge.paid_at,
          title: "Pago registrado",
          detail: `${money.format(Number(charge.amount))} · ${charge.description}`,
          kind: "success",
        });
      }
    }
    return items.sort((left, right) => activityDate(right.at) - activityDate(left.at));
  }, [detailQuery.data]);

  if (detailQuery.isLoading) {
    return <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Cargando ficha 360…</div>;
  }
  if (detailQuery.error || !detailQuery.data || !store) {
    return (
      <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-6">
        <p className="font-semibold text-red-900">No pudimos cargar este negocio.</p>
        <p className="mt-1 text-sm text-red-800">{detailQuery.error instanceof Error ? detailQuery.error.message : "La ficha puede haber sido eliminada o no tienes acceso."}</p>
        <Button className="mt-4" variant="outline" onClick={() => navigate("/directorio")}>Volver al directorio</Button>
      </div>
    );
  }

  const data = detailQuery.data;
  const setup = Number(store.billing_setup_amount ?? 0);
  const monthly = Number(store.billing_monthly_amount ?? 0);
  const yearly = Number(store.billing_yearly_amount ?? 0);
  const { billingSummary } = data;
  const saasUrl = store.origin === "saas" ? saasManagementUrl(store, saasAdminBaseUrl) : null;
  const publicUrl = `${publicSiteUrl}/tienda/${store.slug}/`;
  const isPublic = store.is_visible_in_directory && !store.is_force_hidden && store.editorial_status === "visible";
  const editorialLabel = store.editorial_status === "visible" ? "Revisión aprobada" : store.editorial_status === "review" ? "En revisión" : "Oculta editorial";

  return (
    <div className="space-y-5 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={() => navigate(store.origin === "manual" ? "/directorio/manuales" : "/directorio/saas")} className="text-sm font-medium text-slate-600 hover:text-slate-950">← Volver al directorio</button>
        <div className="flex flex-wrap gap-2">
          <Link to={`/directorio/${store.id}/editar`} className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">Editar ficha</Link>
          {saasUrl ? <a href={saasUrl} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">{saasAdminBaseUrl ? "Abrir panel SaaS ↗" : "Abrir sitio SaaS ↗"}</a> : null}
          {isPublic ? <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center rounded-md bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-700">Ver ficha pública ↗</a> : <span className="inline-flex h-9 items-center rounded-md bg-slate-100 px-3 text-sm font-medium text-slate-500">Sin página pública</span>}
        </div>
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
          {store.logo_url ? <img src={store.logo_url} alt="" className="h-16 w-16 rounded-xl border border-slate-200 object-cover" /> : <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-2xl font-bold text-slate-500">{store.name.slice(0, 1).toUpperCase()}</div>}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl font-bold text-slate-950">{store.name}</h1>
              <Badge tone="neutral">{originLabel(store.origin)}</Badge>
              <Badge tone={store.is_visible_in_directory && !store.is_force_hidden ? "signal" : "neutral"}>{store.is_force_hidden ? "Bloqueado" : store.is_visible_in_directory ? "Visible en web" : "No publicado"}</Badge>
              <Badge tone={store.editorial_status === "visible" ? "signal" : "neutral"}>{editorialLabel}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-500">{[store.category, store.district || store.city, store.province, store.department].filter(Boolean).join(" · ") || "Sin ubicación registrada"}</p>
            <p className="mt-1 text-xs text-slate-400">/{store.slug}/ · Alta {formatDate(store.created_at)}</p>
          </div>
        </div>
        {store.admin_next_action ? (
          <div className={`border-t px-5 py-3 text-sm sm:px-6 ${store.admin_next_action_due_date && store.admin_next_action_due_date < new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10) ? "border-red-100 bg-red-50 text-red-800" : "border-blue-100 bg-blue-50 text-blue-900"}`}>
            <span className="font-semibold">Próxima acción:</span> {store.admin_next_action}
            {store.admin_next_action_due_date ? ` · objetivo ${formatDate(store.admin_next_action_due_date)}` : ""}
          </div>
        ) : null}
        <div className="grid grid-cols-2 divide-x divide-y border-t border-slate-100 sm:grid-cols-4 sm:divide-y-0">
          <Metric label="Cobros pendientes" value={money.format(billingSummary.pending_total)} detail={`${billingSummary.pending_count} conceptos`} />
          <Metric label="Vencidos" value={String(billingSummary.overdue_count)} detail={`${money.format(billingSummary.overdue_total)} por cobrar`} tone={billingSummary.overdue_count > 0 ? "danger" : "normal"} />
          <Metric label="Cobrado registrado" value={money.format(billingSummary.paid_total)} detail={`${billingSummary.paid_count} pagos`} />
          <Metric label="Actividad reciente" value={String(activity.length)} detail="Eventos cargados" />
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.55fr)_minmax(340px,0.85fr)]">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-950">Actividad de la cuenta</h2>
              <p className="mt-1 text-sm text-slate-500">Solicitudes, invitaciones, pagos y recordatorios en orden cronológico.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">Últimos {activity.length} eventos</span>
          </div>
          {activity.length ? (
            <ol className="mt-5 space-y-0">
              {activity.map((item, index) => (
                <li key={item.id} className="relative flex gap-3 pb-5 last:pb-0">
                  {index < activity.length - 1 ? <span className="absolute bottom-0 left-[7px] top-3 w-px bg-slate-200" aria-hidden="true" /> : null}
                  <span className={`relative mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-white ring-1 ${item.kind === "success" ? "bg-emerald-500 ring-emerald-200" : item.kind === "warning" ? "bg-amber-500 ring-amber-200" : "bg-slate-400 ring-slate-200"}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="text-sm font-semibold text-slate-800">{item.title}</p>
                      <time className="text-xs text-slate-400">{formatDate(item.at, true)}</time>
                    </div>
                    <p className="mt-0.5 break-words text-sm text-slate-500">{item.detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          ) : <p className="mt-5 rounded-lg bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">Todavía no hay actividad registrada.</p>}
          {data.charges.length === 50 || data.submissions.length === 30 || data.invitations.length >= 50 ? <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-400">La actividad muestra los registros más recientes (hasta 50 cobros, 30 solicitudes y 50 enlaces).</p> : null}
        </section>

        <div className="space-y-5">
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-bold text-slate-950">Contrato y cobro</h2>
                <p className="mt-1 text-sm text-slate-500">Condiciones vigentes de la cuenta.</p>
              </div>
              {store.billing_is_comped ? <Badge tone="signal">Cortesía</Badge> : <Badge tone={store.billing_status === "active" ? "signal" : "neutral"}>{store.billing_status === "paused" ? "Pausado" : store.billing_status === "cancelled" ? "Cancelado" : "Activo"}</Badge>}
            </div>
            <div className="mt-4 rounded-lg bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{store.billing_plan_name || "Acuerdo personalizado"}</p>
              {typeof store.billing_plan_snapshot?.summary === "string" && store.billing_plan_snapshot.summary ? <p className="mt-2 text-sm text-slate-600">{store.billing_plan_snapshot.summary}</p> : null}
              {Array.isArray(store.billing_plan_snapshot?.features) && store.billing_plan_snapshot.features.length > 0 ? <p className="mt-2 text-xs leading-5 text-slate-500">Incluye: {store.billing_plan_snapshot.features.map(String).join(" · ")}</p> : null}
              {store.billing_is_comped ? <p className="mt-2 text-lg font-bold text-emerald-700">Gratis · sin generar cargos</p> : <div className="mt-2 space-y-1 text-sm text-slate-700">
                {setup > 0 ? <p>Alta: <strong>{money.format(setup)}</strong> · vence {formatDate(store.billing_setup_due_date)}</p> : null}
                {monthly > 0 ? <p>Mensualidad: <strong>{money.format(monthly)}</strong> · desde {formatDate(store.billing_start_date)}</p> : null}
                {yearly > 0 ? <p>Anualidad: <strong>{money.format(yearly)}</strong> · desde {formatDate(store.billing_start_date)}</p> : null}
                {setup === 0 && monthly === 0 && yearly === 0 ? <p>Sin importes de cobro configurados.</p> : null}
              </div>}
              {store.billing_price_note ? <p className="mt-3 border-t border-slate-200 pt-2 text-xs text-slate-500">Motivo / acuerdo: {store.billing_price_note}</p> : null}
              {store.billing_plan_period ? <p className="mt-2 text-xs text-slate-500">Periodicidad del plan: {store.billing_plan_period === "monthly" ? "mensual" : store.billing_plan_period === "yearly" ? "anual" : "pago único"}</p> : null}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <Detail label="Inicio del contrato" value={formatDate(store.billing_start_date)} />
              <Detail label="Cargos registrados" value={String(billingSummary.charge_count)} />
            </div>
            <Link to="/cobros" className="mt-4 inline-flex text-sm font-semibold text-blue-700 hover:text-blue-900">Ir a gestión de cobros →</Link>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-bold text-slate-950">Contacto y origen</h2>
            <div className="mt-4 space-y-3 text-sm">
              <Detail label="Teléfono" value={store.phone || "Sin teléfono"} />
              <Detail label="WhatsApp" value={store.whatsapp || "Sin WhatsApp"} />
              <Detail label="Sitio web" value={store.website_url || store.store_url || "Sin sitio web"} />
              <Detail label="Dirección" value={[store.address, store.district, store.province, store.department].filter(Boolean).join(", ") || "Sin dirección"} />
              <Detail label="Origen" value={store.origin === "manual" ? "Gestión manual de Vimos" : `SaaS${store.saas_store_id ? ` · ID ${store.saas_store_id}` : ""}`} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/invitaciones" className="inline-flex h-9 items-center rounded-md border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">Ver invitaciones</Link>
              <Link to="/solicitudes" className="inline-flex h-9 items-center rounded-md border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">Ver solicitudes</Link>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-950">Seguimiento interno</h2>
              <p className="mt-1 text-sm text-slate-500">Notas visibles solo para el equipo administrativo.</p>
            </div>
            <label className="mt-4 block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">Próxima acción</span>
              <input
                className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-slate-50"
                value={nextAction}
                onChange={(event) => { setNextAction(event.target.value); setSaved(false); }}
                maxLength={120}
                placeholder="Ej. Llamar para renovar"
                disabled={!userCanEdit}
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">Fecha objetivo</span>
              <input
                type="date"
                className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-slate-50"
                value={dueDate}
                onChange={(event) => { setDueDate(event.target.value); setSaved(false); }}
                disabled={!userCanEdit || !nextAction.trim()}
              />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold text-slate-600">Notas</span>
              <textarea
                className="min-h-28 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:bg-slate-50"
                value={notes}
                onChange={(event) => { setNotes(event.target.value); setSaved(false); }}
                maxLength={5000}
                placeholder="Contexto útil para el próximo seguimiento…"
                disabled={!userCanEdit}
              />
              <span className="mt-1 block text-right text-[11px] text-slate-400">{notes.length}/5000</span>
            </label>
            {saveContext.error ? <p className="mt-2 text-sm text-red-600">{saveContext.error instanceof Error ? saveContext.error.message : "No se pudo guardar el seguimiento."}</p> : null}
            {saved ? <p className="mt-2 text-sm text-emerald-700" role="status">Seguimiento guardado.</p> : null}
            {userCanEdit ? <Button className="mt-3 w-full" onClick={() => saveContext.mutate()} isLoading={saveContext.isPending}>Guardar seguimiento</Button> : <p className="mt-3 text-xs text-slate-500">Tu rol permite consultar la ficha, pero no editar el seguimiento interno.</p>}
            {store.admin_context_updated_at ? <p className="mt-2 text-xs text-slate-400">Actualizado {formatDate(store.admin_context_updated_at, true)}</p> : null}
          </section>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, detail, tone = "normal" }: { label: string; value: string; detail: string; tone?: "normal" | "danger" }) {
  return (
    <div className="px-4 py-3 sm:px-5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 truncate text-lg font-bold tabular-nums ${tone === "danger" ? "text-red-700" : "text-slate-900"}`}>{value}</p>
      <p className="mt-0.5 truncate text-xs text-slate-500">{detail}</p>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <span className="break-words text-slate-700">{value}</span>
    </div>
  );
}
